import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Scan, Package, Truck, CheckCircle2, Clock, AlertTriangle, 
  Play, Pause, Square, RefreshCw, Download, Search,
  ArrowLeft, MapPin, Keyboard
} from 'lucide-react';
import api from '../../utils/api';

const ScanConsole = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id');
  
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [session, setSession] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [scanEvents, setScanEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanMode, setScanMode] = useState('loading'); // 'loading' or 'unloading'
  
  // Manual entry
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualQRCode, setManualQRCode] = useState('');
  const [manualItemId, setManualItemId] = useState('');
  // USB barcode scanner mode — listens globally for keypress sequences
  const [usbScannerEnabled, setUsbScannerEnabled] = useState(false);
  const usbBufferRef = useRef({ buffer: '', lastKey: 0 });
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    scanned: 0,
    pending: 0,
    damaged: 0,
    missing: 0
  });

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (jobId) {
      loadJobDetails(jobId);
    }
  }, [jobId]);

  useEffect(() => {
    if (session?.id) {
      // Poll for updates every 5 seconds
      const interval = setInterval(() => {
        loadScanEvents(session.id);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [session?.id]);

  const loadJobs = async () => {
    try {
      const response = await api.get('/jobs');
      const activeJobs = (response.data || []).filter(j => 
        ['confirmed', 'in_progress', 'loading', 'unloading'].includes(j.status)
      );
      setJobs(activeJobs);
      
      // If no jobId param but we have jobs, select first one
      if (!jobId && activeJobs.length > 0) {
        navigate(`/control-panel/scan-console?job_id=${activeJobs[0].id}`);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJobDetails = async (id) => {
    try {
      const [jobRes, inventoryRes] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/inventory/enhanced/${id}`)
      ]);
      
      setSelectedJob(jobRes.data);
      setInventory(inventoryRes.data?.items || []);
      
      // Check for existing active session
      try {
        const sessionsRes = await api.get(`/scan/sessions/job/${id}`);
        const activeSessions = (sessionsRes.data || []).filter(s => s.status !== 'completed');
        if (activeSessions.length > 0) {
          setSession(activeSessions[0]);
          setScanMode(activeSessions[0].type);
          loadScanEvents(activeSessions[0].id);
        }
      } catch (e) {
        // No sessions yet
      }
      
      calculateStats(inventoryRes.data?.items || [], []);
    } catch (error) {
      toast.error('Failed to load job details');
    }
  };

  const loadScanEvents = async (sessionId) => {
    try {
      const response = await api.get(`/scan/events/session/${sessionId}`);
      setScanEvents(response.data || []);
      calculateStats(inventory, response.data || []);
    } catch (error) {
      console.error('Error loading scan events:', error);
    }
  };

  const calculateStats = (items, events) => {
    const scannedIds = new Set(events.map(e => e.item_id));
    const damaged = events.filter(e => e.condition === 'damaged').length;
    const missing = events.filter(e => e.condition === 'missing').length;
    
    setStats({
      total: items.length,
      scanned: scannedIds.size,
      pending: items.length - scannedIds.size,
      damaged,
      missing
    });
  };

  const startSession = async () => {
    if (!selectedJob) {
      toast.error('Please select a job first');
      return;
    }
    
    try {
      const response = await api.post('/scan/sessions', {
        job_id: selectedJob.id,
        type: scanMode,
        location: scanMode === 'loading' 
          ? selectedJob.from_address?.formatted || 'Pickup Location'
          : selectedJob.to_address?.formatted || 'Delivery Location'
      });
      
      setSession(response.data);
      toast.success(`${scanMode === 'loading' ? 'Loading' : 'Unloading'} session started`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start session');
    }
  };

  const pauseSession = async () => {
    if (!session) return;
    
    try {
      await api.put(`/scan/sessions/${session.id}`, { status: 'paused' });
      setSession(prev => ({ ...prev, status: 'paused' }));
      toast.info('Session paused');
    } catch (error) {
      toast.error('Failed to pause session');
    }
  };

  const resumeSession = async () => {
    if (!session) return;
    
    try {
      await api.put(`/scan/sessions/${session.id}`, { status: 'active' });
      setSession(prev => ({ ...prev, status: 'active' }));
      toast.success('Session resumed');
    } catch (error) {
      toast.error('Failed to resume session');
    }
  };

  const completeSession = async () => {
    if (!session) return;
    
    // Check if all items scanned
    if (stats.pending > 0) {
      const confirm = window.confirm(
        `${stats.pending} items haven't been scanned yet. Complete anyway?`
      );
      if (!confirm) return;
    }
    
    try {
      await api.post(`/scan/sessions/${session.id}/complete`);
      setSession(prev => ({ ...prev, status: 'completed' }));
      toast.success('Session completed! Report generated.');
      
      // Generate report
      try {
        await api.post(`/scan/reports/${session.id}`);
      } catch (e) {
        console.error('Report generation failed:', e);
      }
    } catch (error) {
      toast.error('Failed to complete session');
    }
  };

  const recordScan = async (itemId, qrCode, condition = 'good') => {
    if (!session || session.status !== 'active') {
      toast.error('Please start or resume the session first');
      return;
    }
    
    try {
      const response = await api.post('/scan/events', {
        session_id: session.id,
        item_id: itemId,
        qr_code: qrCode,
        condition,
        notes: ''
      });
      
      // Use functional update so we don't double-count when multiple scans
      // land in the same render cycle (race between barcode scanner bursts).
      setScanEvents(prev => {
        const next = [response.data, ...prev];
        calculateStats(inventory, next);
        return next;
      });
      toast.success('Item scanned');
      
      // Close manual entry dialog
      setShowManualEntry(false);
      setManualQRCode('');
      setManualItemId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record scan');
    }
  };

  /**
   * USB barcode-scanner global listener.
   *
   * Warehouse USB scanners type the barcode characters rapidly followed by
   * Enter. We capture keypresses globally, buffer them, and on Enter look up
   * the accumulated string as a QR code.
   *
   * Gated by usbScannerEnabled so typing in form fields doesn't trigger it.
   */
  useEffect(() => {
    if (!usbScannerEnabled || !session || session.status !== 'active') return;

    const onKey = (e) => {
      // Ignore if user is typing in an input/textarea/select
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
        return;
      }

      const now = Date.now();
      const buf = usbBufferRef.current;
      // Reset buffer if >200ms since last key (human typing, not scanner)
      if (now - buf.lastKey > 200) buf.buffer = '';
      buf.lastKey = now;

      if (e.key === 'Enter') {
        const code = buf.buffer.trim();
        buf.buffer = '';
        if (code.length >= 3) {
          // Look up and record
          (async () => {
            try {
              const response = await api.get(`/scan/lookup/${encodeURIComponent(code)}`);
              if (response.data?.item) {
                recordScan(response.data.item.id, code);
              } else {
                toast.error(`QR code not found: ${code}`);
              }
            } catch {
              toast.error(`QR code not found: ${code}`);
            }
          })();
        }
      } else if (e.key.length === 1) {
        buf.buffer += e.key;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usbScannerEnabled, session?.status]);

  /** Download the full scan log as CSV. */
  const exportCsv = () => {
    if (!session) {
      toast.error('No active session to export');
      return;
    }
    const rows = [
      ['Scanned At', 'Item Name', 'Category', 'Quantity', 'QR Code', 'Condition', 'Notes'],
      ...scanEvents.map(e => {
        const item = inventory.find(i => i.id === e.item_id) || {};
        return [
          new Date(e.scanned_at).toISOString(),
          item.item_name || 'Unknown',
          item.category || '',
          item.quantity || '',
          e.qr_code || '',
          e.condition || '',
          (e.notes || '').replace(/"/g, '""'),
        ];
      }),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? '')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const jobNum = selectedJob?.job_number || 'job';
    const dateStamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `scan-log-${jobNum}-${session.type}-${dateStamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${scanEvents.length} scans as CSV`);
  };

  const lookupQRCode = async () => {
    if (!manualQRCode.trim()) {
      toast.error('Please enter a QR code');
      return;
    }
    
    try {
      const response = await api.get(`/scan/lookup/${manualQRCode}`);
      if (response.data?.item) {
        recordScan(response.data.item.id, manualQRCode);
      } else {
        toast.error('Item not found');
      }
    } catch (error) {
      toast.error('QR code not found');
    }
  };

  const getItemScanStatus = (itemId) => {
    const event = scanEvents.find(e => e.item_id === itemId);
    if (!event) return 'pending';
    return event.condition;
  };

  const progressPercent = stats.total > 0 ? Math.round((stats.scanned / stats.total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="animate-spin h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/control-panel/jobs')}>
            <ArrowLeft size={20} className="mr-2" />
            Back to Jobs
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Scan className="text-primary" />
              Scan Console
            </h1>
            <p className="text-muted-foreground">Track item loading and unloading in real-time</p>
          </div>
        </div>
      </div>

      {/* Job Selector */}
      {!selectedJob && (
        <Card>
          <CardHeader>
            <CardTitle>Select a Job</CardTitle>
            <CardDescription>Choose a job to start scanning items</CardDescription>
          </CardHeader>
          <CardContent>
            {jobs.length === 0 ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  No active jobs found. Jobs must be in "Confirmed" or "In Progress" status to use the scan console.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid gap-3">
                {jobs.map(job => (
                  <Button
                    key={job.id}
                    variant="outline"
                    className="justify-start h-auto py-3"
                    onClick={() => navigate(`/control-panel/scan-console?job_id=${job.id}`)}
                  >
                    <div className="text-left">
                      <div className="font-medium">{job.job_number || job.id.slice(0, 8)}</div>
                      <div className="text-sm text-muted-foreground">
                        {job.from_address?.city} → {job.to_address?.city}
                      </div>
                    </div>
                    <Badge variant="outline" className="ml-auto">{job.status}</Badge>
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {selectedJob && (
        <>
          {/* Job Info Bar */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg">{selectedJob.job_number}</span>
                      <Badge>{selectedJob.status}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {selectedJob.from_address?.city} → {selectedJob.to_address?.city}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {stats.total} items
                      </span>
                    </div>
                  </div>
                </div>
                <Select value={scanMode} onValueChange={setScanMode} disabled={session?.status === 'active'}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="loading">
                      <span className="flex items-center gap-2">
                        <Truck size={16} /> Loading
                      </span>
                    </SelectItem>
                    <SelectItem value="unloading">
                      <span className="flex items-center gap-2">
                        <Package size={16} /> Unloading
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Progress & Controls */}
          <div className="grid md:grid-cols-3 gap-4">
            {/* Progress Card */}
            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Scan Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span>{stats.scanned} of {stats.total} items scanned</span>
                    <span className="font-bold text-primary">{progressPercent}%</span>
                  </div>
                  <Progress value={progressPercent} className="h-3" />
                  
                  <div className="grid grid-cols-4 gap-2 pt-2">
                    <div className="text-center p-2 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{stats.scanned}</div>
                      <div className="text-xs text-green-700">Scanned</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-600">{stats.pending}</div>
                      <div className="text-xs text-gray-700">Pending</div>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <div className="text-2xl font-bold text-amber-600">{stats.damaged}</div>
                      <div className="text-xs text-amber-700">Damaged</div>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{stats.missing}</div>
                      <div className="text-xs text-red-700">Missing</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Session Controls */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Session Controls</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!session ? (
                  <Button className="w-full" size="lg" onClick={startSession}>
                    <Play size={20} className="mr-2" />
                    Start {scanMode === 'loading' ? 'Loading' : 'Unloading'}
                  </Button>
                ) : (
                  <>
                    {session.status === 'active' ? (
                      <Button variant="outline" className="w-full" onClick={pauseSession}>
                        <Pause size={20} className="mr-2" />
                        Pause Session
                      </Button>
                    ) : session.status === 'paused' ? (
                      <Button className="w-full" onClick={resumeSession}>
                        <Play size={20} className="mr-2" />
                        Resume Session
                      </Button>
                    ) : null}
                    
                    {session.status !== 'completed' && (
                      <Button 
                        variant="default" 
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={completeSession}
                      >
                        <CheckCircle2 size={20} className="mr-2" />
                        Complete Session
                      </Button>
                    )}
                  </>
                )}

                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => setShowManualEntry(true)}
                  disabled={!session || session.status !== 'active'}
                >
                  <Search size={20} className="mr-2" />
                  Manual Entry
                </Button>

                <Button
                  variant={usbScannerEnabled ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => setUsbScannerEnabled(v => !v)}
                  disabled={!session || session.status !== 'active'}
                  data-testid="usb-scanner-toggle"
                >
                  <Keyboard size={20} className="mr-2" />
                  USB Scanner: {usbScannerEnabled ? 'ON' : 'OFF'}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={exportCsv}
                  disabled={!session || scanEvents.length === 0}
                  data-testid="export-csv-btn"
                >
                  <Download size={20} className="mr-2" />
                  Export CSV ({scanEvents.length})
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Paused / Completed / USB-enabled banners */}
          {session?.status === 'paused' && (
            <Alert className="border-amber-300 bg-amber-50" data-testid="paused-banner">
              <Pause className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Session is paused. Scans are disabled until you resume.
              </AlertDescription>
            </Alert>
          )}
          {session?.status === 'completed' && (
            <Alert className="border-green-300 bg-green-50" data-testid="completed-banner">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Session completed. {stats.scanned} of {stats.total} items scanned.
                {' '}<button onClick={exportCsv} className="underline font-medium">Download CSV report</button>.
              </AlertDescription>
            </Alert>
          )}
          {usbScannerEnabled && session?.status === 'active' && (
            <Alert className="border-blue-300 bg-blue-50" data-testid="usb-active-banner">
              <Keyboard className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                USB barcode scanner listening. Point your scanner at a QR code — no need to click anything.
              </AlertDescription>
            </Alert>
          )}

          {/* Inventory List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Inventory Items</span>
                <Badge variant="outline">{inventory.length} items</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {inventory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No inventory items found for this job
                  </p>
                ) : (
                  inventory.map(item => {
                    const status = getItemScanStatus(item.id);
                    return (
                      <div 
                        key={item.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          status === 'good' ? 'bg-green-50 border-green-200' :
                          status === 'damaged' ? 'bg-amber-50 border-amber-200' :
                          status === 'missing' ? 'bg-red-50 border-red-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {status === 'pending' ? (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <Clock size={16} className="text-gray-500" />
                            </div>
                          ) : status === 'good' ? (
                            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                              <CheckCircle2 size={16} className="text-white" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                              <AlertTriangle size={16} className="text-white" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{item.item_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {item.category} • Qty: {item.quantity}
                              {item.qr_code && <span className="ml-2">📱 {item.qr_code.slice(0, 8)}...</span>}
                            </div>
                          </div>
                        </div>
                        
                        {status === 'pending' && session?.status === 'active' && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => recordScan(item.id, item.qr_code || item.id, 'good')}
                            >
                              <CheckCircle2 size={14} className="mr-1" />
                              OK
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-amber-600 border-amber-300"
                              onClick={() => recordScan(item.id, item.qr_code || item.id, 'damaged')}
                            >
                              Damaged
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-300"
                              onClick={() => recordScan(item.id, item.qr_code || item.id, 'missing')}
                            >
                              Missing
                            </Button>
                          </div>
                        )}
                        
                        {status !== 'pending' && (
                          <Badge variant={
                            status === 'good' ? 'default' : 
                            status === 'damaged' ? 'secondary' : 'destructive'
                          }>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Badge>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Scans */}
          {scanEvents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock size={20} />
                  Recent Scans
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {scanEvents.slice(0, 10).map(event => {
                    const item = inventory.find(i => i.id === event.item_id);
                    return (
                      <div key={event.id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 size={16} className="text-green-500" />
                          <span className="font-medium">{item?.item_name || 'Unknown Item'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Badge variant="outline" className="text-xs">
                            {event.condition}
                          </Badge>
                          <span>{new Date(event.scanned_at).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Manual Entry Dialog */}
      <Dialog open={showManualEntry} onOpenChange={setShowManualEntry}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Scan Entry</DialogTitle>
            <DialogDescription>
              Enter a QR code or select an item manually
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">QR Code</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={manualQRCode}
                  onChange={(e) => setManualQRCode(e.target.value)}
                  placeholder="Enter or scan QR code"
                />
                <Button onClick={lookupQRCode}>
                  <Search size={16} />
                </Button>
              </div>
            </div>
            
            <div className="text-center text-sm text-muted-foreground">— or —</div>
            
            <div>
              <label className="text-sm font-medium">Select Item</label>
              <Select value={manualItemId} onValueChange={setManualItemId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose an item..." />
                </SelectTrigger>
                <SelectContent>
                  {inventory.filter(i => getItemScanStatus(i.id) === 'pending').map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.item_name} ({item.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {manualItemId && (
              <div className="flex gap-2">
                <Button 
                  className="flex-1" 
                  onClick={() => recordScan(manualItemId, '', 'good')}
                >
                  <CheckCircle2 size={16} className="mr-2" />
                  Mark as OK
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 text-amber-600"
                  onClick={() => recordScan(manualItemId, '', 'damaged')}
                >
                  Damaged
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 text-red-600"
                  onClick={() => recordScan(manualItemId, '', 'missing')}
                >
                  Missing
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScanConsole;
